import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Global momentum scrolling — desktop only.
 *
 * We intentionally skip Lenis on touch devices because:
 * 1. iOS Safari's native rubber-band + momentum feels better than any JS smoother.
 * 2. Lenis's touch handling can race with anchor clicks, occasionally
 *    swallowing the first tap on section CTAs.
 * 3. Every frame spent by Lenis is a frame not spent hydrating the page.
 */
export default function useSmoothScroll() {
  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(hover: none) and (pointer: coarse)").matches ||
        "ontouchstart" in window ||
        (navigator.maxTouchPoints || 0) > 0);
    if (isTouch) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.6,
      anchors: true,
    });

    window.__lenis = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);
}
